import {
  AccountInfo,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  IPublicClientApplication,
  InteractionRequiredAuthError,
} from '@azure/msal-browser'

import { loginRequest, msalService } from '../AuthConfig'

type PendingSignIn = {
  account: AccountInfo
  instance: IPublicClientApplication
  token: Promise<string>
  resolve: (accessToken: string) => void
}

let redirect: Promise<never> | null = null
let pendingSignIn: PendingSignIn | null = null
const sessionExpiryListeners = new Set<() => void>()

export async function getAuthToken(
  msalAccount?: AccountInfo,
  msalInstance?: IPublicClientApplication,
): Promise<string> {
  const account = msalAccount || msalService.account
  const instance = msalInstance || msalService.instance

  try {
    return await acquireTokenSilently(account, instance)
  } catch (error) {
    if (!needsInteractiveSignIn(error)) throw error
    console.warn('Silent token renewal failed, sign-in needed:', error.errorCode)
    // A redirect now could drop the change that asked for this token, so wait for the
    // user to sign in from the session-expired dialog instead.
    return waitForSignIn(account, instance)
  }
}

// Runs before any route calls the API, when nothing can be lost yet. An expired session
// is renewed with a full-page redirect, which MSAL brings back to the same URL.
export async function renewSessionOnLoad(
  account: AccountInfo,
  instance: IPublicClientApplication,
): Promise<void> {
  try {
    await acquireTokenSilently(account, instance)
  } catch (error) {
    if (!needsInteractiveSignIn(error)) throw error
    console.warn('Silent token renewal failed on load, redirecting to sign in:', error.errorCode)
    redirect ??= instance
      .acquireTokenRedirect({ ...loginRequest, account })
      // The page is navigating away, so keep callers waiting until it unloads.
      .then(() => new Promise<never>(() => {}))
      .catch((redirectError) => {
        redirect = null
        throw redirectError
      })
    return redirect
  }
}

export function isSessionExpired(): boolean {
  return pendingSignIn !== null
}

export function subscribeToSessionExpiry(listener: () => void): () => void {
  sessionExpiryListeners.add(listener)
  return () => sessionExpiryListeners.delete(listener)
}

// Call this straight from a click handler. MSAL opens the popup synchronously, and
// Safari only allows popups that open during a click.
export function signInAgain(): Promise<void> {
  if (!pendingSignIn) return Promise.resolve()
  const { account, instance, resolve } = pendingSignIn
  const request = { ...loginRequest, account }

  return instance.acquireTokenPopup(request).then(
    (response) => {
      pendingSignIn = null
      sessionExpiryListeners.forEach((listener) => listener())
      resolve(response.accessToken)
    },
    (error) => {
      // Pop-ups are blocked for this site, so a redirect is the only way left to sign in.
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === BrowserAuthErrorCodes.popupWindowError
      ) {
        return instance.acquireTokenRedirect(request)
      }
      throw error
    },
  )
}

// Safari blocks the third-party cookies the hidden renewal iframe needs. Entra then
// answers login_required, or never answers and MSAL times out waiting for the iframe.
function needsInteractiveSignIn(
  error: unknown,
): error is InteractionRequiredAuthError | BrowserAuthError {
  return (
    error instanceof InteractionRequiredAuthError ||
    (error instanceof BrowserAuthError &&
      error.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout)
  )
}

function waitForSignIn(account: AccountInfo, instance: IPublicClientApplication): Promise<string> {
  if (!pendingSignIn) {
    let resolve: (accessToken: string) => void = () => {}
    const token = new Promise<string>((resolveToken) => {
      resolve = resolveToken
    })
    pendingSignIn = { account, instance, token, resolve }
    sessionExpiryListeners.forEach((listener) => listener())
  }
  return pendingSignIn.token
}

async function acquireTokenSilently(
  account: AccountInfo,
  instance: IPublicClientApplication,
): Promise<string> {
  const response = await instance.acquireTokenSilent({ ...loginRequest, account })
  return response.accessToken
}
